import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import moment from "moment/moment";
import html2canvas from "html2canvas";
import newLogo from "../../assets/images/curebayIcon.svg";
import PropTypes from "prop-types";
import apiService from "../../services/actions";
import { fromApiClinicalHistory } from "../../utils/clinicalHistoryUtils";

function TRFpopup(props) {
  const [genaratePDFData, setGenaratePDFData] = useState(
    Array.isArray(props?.data) ? props.data : [],
  );
  const [vialsData, setVialsData] = useState(
    Array.isArray(props?.vialsData) ? props.vialsData : [],
  );
  const [clinicalData, setClinicalData] = useState(null);

  const loginObjStr = localStorage.getItem("loginObj");
  const loginObj = JSON.parse(loginObjStr || "null");
  const userCode = loginObj?.userSession?.userCode || null;

  const [clinicalHistory, setClinicalHistory] = useState("");
  const [remark, setRemark] = useState("");
  const [initialValues, setInitialValues] = useState({
    clinicalHistory: "",
    remark: "",
  });

  useEffect(() => {
    setGenaratePDFData(Array.isArray(props?.data) ? props.data : []);
  }, [props?.data]);

  useEffect(() => {
    if (Array.isArray(props?.vialsData)) {
      setVialsData(props.vialsData);
    }
  }, [props?.vialsData]);

  useEffect(() => {
    const ch = genaratePDFData?.[0]?.clinicalHistory || "";
    const rm = genaratePDFData?.[0]?.remark || "";
    setClinicalHistory(ch);
    setRemark(rm);
    setInitialValues({ clinicalHistory: ch, remark: rm });
  }, [genaratePDFData]);

  useEffect(() => {
    const patientCode =
      vialsData?.flat?.()?.[0]?.patientid ||
      genaratePDFData?.[0]?.patientCode ||
      genaratePDFData?.[0]?.patientId ||
      genaratePDFData?.[0]?.patientid;

    if (!patientCode) {
      setClinicalData(null);
      return;
    }

    let cancelled = false;

    const loadClinical = async () => {
      try {
        const chRes = await apiService.getPatientClinicalHistory(patientCode);
        const saved =
          chRes?.data?.successList?.[0]?.clinicalHistory ||
          chRes?.data?.successList?.[0];
        if (!cancelled) {
          setClinicalData(saved ? fromApiClinicalHistory(saved) : null);
        }
      } catch (e) {
        if (!cancelled) setClinicalData(null);
      }
    };

    loadClinical();

    return () => {
      cancelled = true;
    };
  }, [genaratePDFData, vialsData]);

  const totalVialRows = useMemo(() => {
    if (!Array.isArray(vialsData)) return 0;
    return vialsData.reduce((sum, group) => {
      if (!Array.isArray(group)) return sum;
      return sum + group.length;
    }, 0);
  }, [vialsData]);

  const compactPdfLayout = totalVialRows >= 9;

  const groupedData = genaratePDFData.reduce((acc, item) => {
    const labKey = item.partnerLabName || "—";
    if (!acc[labKey]) {
      acc[labKey] = [];
    }
    acc[labKey].push(item);
    return acc;
  }, {});

  const groupedLabs = Object.entries(groupedData).map(([partnerLabName, tests]) => {
    const packageGroups = tests.reduce((acc, test) => {
      const key = test.packageName || test.labTestName;
      if (!acc[key]) {
        acc[key] = {
          packageName: test.packageName || null,
          packageCode: test.packageCode || null,
          tests: [],
          testCode: test.labTestCode || null,
        };
      }
      if (test.labTestName) acc[key].tests.push(test.labTestName);
      return acc;
    }, {});

    return {
      partnerLabName,
      packages: Object.values(packageGroups),
    };
  });

  const updateTrfIfChanged = async () => {
    const orderId = genaratePDFData?.[0]?.orderId;
    const hasChanged =
      clinicalHistory !== initialValues.clinicalHistory ||
      remark !== initialValues.remark;

    if (!hasChanged || !orderId) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_NEW}GenerateTrf/update/${orderId}`,
        {
          clinicalHistory,
          remark,
          modifiedBy: userCode,
        },
      );
      setInitialValues({ clinicalHistory, remark });
    } catch (err) {
      console.error("Failed to update TRF", err);
    }
  };

  const downloadPDF = async () => {
    await updateTrfIfChanged();

    try {
      const input = document.getElementById("trf-content");
      if (!input) return;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 20;
      const marginTop = 15;
      const marginBottom = 15;
      const usablePageHeight = pageHeight - marginTop - marginBottom;

      const canvas = await html2canvas(input, {
        scale: 1.8,
        useCORS: true,
        logging: false,
        allowTaint: false,
        backgroundColor: "#ffffff",
        windowWidth: input.scrollWidth,
        windowHeight: input.scrollHeight,
        onclone: (clonedDoc) => {
          const root = clonedDoc.getElementById("trf-content");
          if (!root) return;

          root.querySelectorAll("textarea").forEach((el) => {
            const value = el.value || "";
            const computed = clonedDoc.defaultView?.getComputedStyle(el);
            const replacement = clonedDoc.createElement("div");
            replacement.className = `${el.className} whitespace-pre-wrap break-words`;
            if (computed?.height && computed.height !== "auto" && computed.height !== "0px") {
              replacement.style.minHeight = computed.height;
            } else {
              replacement.style.minHeight = "40px";
            }
            replacement.textContent = value || "\u00A0";
            el.replaceWith(replacement);
          });
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.82);
      const imgAlias = "trf-snapshot";
      const imgWidth = pageWidth - marginX * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const forceOnePage = compactPdfLayout === true;

      if (forceOnePage && imgHeight > usablePageHeight) {
        const scaleToFit = usablePageHeight / imgHeight;
        const scaledWidth = imgWidth * scaleToFit;
        const scaledHeight = imgHeight * scaleToFit;
        const x = (pageWidth - scaledWidth) / 2;
        const y = marginTop;

        pdf.addImage(imgData, "JPEG", x, y, scaledWidth, scaledHeight, imgAlias, "FAST");
        pdf.save(`TRF-${genaratePDFData[0]?.orderId || "document"}.pdf`);
        return;
      }

      const x = (pageWidth - imgWidth) / 2;
      const y = marginTop;

      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight, imgAlias, "FAST");

      for (let page = 1; page * usablePageHeight < imgHeight; page += 1) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          x,
          y - page * usablePageHeight,
          imgWidth,
          imgHeight,
          imgAlias,
          "FAST",
        );
      }

      pdf.save(`TRF-${genaratePDFData[0]?.orderId || "document"}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  const yn = (v) => (v ? "Yes" : "No");
  const genderLower = genaratePDFData[0]?.gender?.toString().toLowerCase();
  const isMalePatient = genderLower === "m" || genderLower === "male";

  const ClinicalField = ({ label, value }) => (
    <div className="flex gap-1">
      <span className="text-gray-700">{label}:</span>
      <span className="font-bold text-gray-600">{value}</span>
    </div>
  );

  ClinicalField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  };

  const mh = clinicalData?.medicalHistory || {};
  const dh = clinicalData?.drugHistory || {};
  const vm = clinicalData?.viralMarkerHistory || {};
  const sc = clinicalData?.specialConditions || {};
  const fs = clinicalData?.fastingStatus || {};
  const fastingText = fs.isFasting
    ? `Fasting${fs.hours ? ` – ${fs.hours} hrs` : ""}`
    : "Non-fasting";

  return (
    <>
      <div
        id="trf-content"
        className="px-2 ml-4 bg-white max-w-7xl mx-auto p-5"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        <div className="px-2 mb-5">
          <div className="flex justify-between items-start ml-1">
            <div className="mr-4 mt-4">
              <img alt="logo" src={newLogo} width={260} />
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Diagnostic TRF</h1>
              <p className="text-sm font-semibold text-gray-700 m-0">
                {genaratePDFData[0]?.assignedCheName}
              </p>
              <p className="text-sm text-gray-600 m-0">
                Clinic No: <span className="text-black font-semibold">{genaratePDFData[0]?.assignedCheMobile}</span>
              </p>
            </div>
          </div>
        </div>

        <div
          className="center-vertically bg-brand-cerulean flex items-center justify-center text-white rounded-lg py-2 mb-4"
          style={{ backgroundColor: "#00b2dd" }}
        >
          <p className="text-base font-medium m-0">Details</p>
        </div>

        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <div className="flex-1">
              <span className="text-gray-600 font-bold text-sm">Patient Name:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.patientName}</span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-600 font-bold text-sm">Age/Gender:</span>
              <span className="text-black ml-2 text-sm">
                {genaratePDFData[0]?.patientAge}/
                {(() => {
                  const g = genaratePDFData[0]?.gender?.toString().toLowerCase();
                  if (g === "m" || g === "male") return "Male";
                  if (g === "f" || g === "female") return "Female";
                  return "Others";
                })()}
              </span>
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div className="flex-1">
              <span className="text-gray-600 font-bold text-sm">Mobile No:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.mobileNumber}</span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-600 font-bold text-sm">Billing eClinic:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.eclinicName || "—"}</span>
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div className="flex-1">
              <span className="text-gray-600 font-bold text-sm">Assigned eClinic:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.assignedCheName || "—"}</span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-600 font-bold text-sm">Billing Date:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.date}</span>
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div className="flex-1">
              <span className="text-gray-600 font-bold text-sm">Order Id:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.orderId}</span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-600 font-bold text-sm">Transaction Id:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.transactionId}</span>
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div className="flex-1">
              {genaratePDFData[0]?.sampleCollectionDate && (
                <span className="text-gray-600 font-bold text-sm">Sample Collection Date & Time:</span>
              )}
              <span className="text-black ml-2 text-sm">
                {genaratePDFData[0]?.sampleCollectionDate && moment(
                  `${genaratePDFData[0].sampleCollectionDate.split(" ")[0]} ${genaratePDFData[0].sampleCollectionTime}`,
                  "YYYY-MM-DD HH:mm",
                ).format("DD/MM/YYYY hh:mm A")}
              </span>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-600 font-bold text-sm">Referred By:</span>
              <span className="text-black ml-2 text-sm">{genaratePDFData[0]?.preferedDoctor || "—"}</span>
            </div>
          </div>
        </div>

        <hr className="my-4 border-t border-gray-300" />

        <div className="my-4 overflow-x-auto">
          {groupedLabs && groupedLabs.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-gray-300 p-3 text-left font-bold text-gray-700">Partner Lab</th>
                  <th className="border border-gray-300 p-3 text-left font-bold text-gray-700">Package Name / Test Name</th>
                  <th className="border border-gray-300 p-3 text-left font-bold text-gray-700">Package Code / Test Code</th>
                </tr>
              </thead>
              <tbody>
                {groupedLabs.map((lab, i) =>
                  lab.packages.map((pkg, j) => (
                    <tr key={`${i}-${j}`} className="bg-white">
                      {j === 0 && (
                        <td
                          rowSpan={lab.packages.length}
                          className="border border-gray-300 p-3 align-top font-medium bg-white"
                        >
                          {lab.partnerLabName}
                        </td>
                      )}
                      <td className="border border-gray-300 p-3">
                        {pkg.packageName
                          ? `${pkg.packageName} (${pkg.tests.join(" | ")})`
                          : pkg.tests.join(" | ")}
                      </td>
                      <td className="border border-gray-300 p-3">
                        {pkg.packageCode || pkg.testCode || "—"}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-8 text-gray-600">No test data available</p>
          )}
        </div>

        {vialsData && Array.isArray(vialsData) && vialsData.length > 0 && (
          <div className="my-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-blue-50">
                  <th
                    className={`border border-gray-300 text-left font-bold text-gray-700 whitespace-nowrap ${compactPdfLayout ? "px-2 py-1 text-xs" : "p-3"}`}
                  >
                    Vial
                  </th>
                  <th
                    className={`border border-gray-300 text-left font-bold text-gray-700 whitespace-nowrap ${compactPdfLayout ? "px-2 py-1 text-xs" : "p-3"}`}
                  >
                    Bar Code
                  </th>
                  <th
                    style={{ width: "55%" }}
                    className={`border border-gray-300 text-left font-bold text-gray-700 whitespace-nowrap ${compactPdfLayout ? "px-2 py-1 text-xs" : "p-3"}`}
                  >
                    Test
                  </th>
                </tr>
              </thead>
              <tbody>
                {vialsData.map((vialGroup, groupIndex) =>
                  Array.isArray(vialGroup)
                    ? vialGroup.map((row, rowIndex) => (
                        <tr key={`${groupIndex}-${rowIndex}`} className="bg-white">
                          <td
                            className={`border border-gray-300 text-blue-600 whitespace-nowrap ${compactPdfLayout ? "px-2 py-1 text-xs leading-tight" : "p-3"}`}
                          >
                            {row.vialsName || row.category || "—"}
                          </td>
                          <td
                            className={`border border-gray-300 ${compactPdfLayout ? "px-2 py-1 text-xs leading-tight" : "p-3"}`}
                          >
                            {row.barcodeId || "—"}
                          </td>
                          <td
                            className={`border border-gray-300 w-[55%] ${compactPdfLayout ? "px-2 py-1 text-xs leading-tight" : "p-3"}`}
                          >
                            {row.labtestname || row.packageName || "—"}
                          </td>
                        </tr>
                      ))
                    : null,
                )}
              </tbody>
            </table>
          </div>
        )}

        {clinicalData && (
          <>
            <div
              className="center-vertically bg-brand-cerulean flex items-center justify-center text-white rounded-lg mt-3"
              style={{ backgroundColor: "#00b2dd" }}
            >
              <p className={compactPdfLayout ? "py-0.5 pb-1 text-sm" : "py-1 pb-2"}>
                Patient Clinical History
              </p>
            </div>
            <div className="border border-gray-300 rounded-md mt-2 text-sm overflow-hidden bg-white">
              <div className="grid grid-cols-3">
                <div className="space-y-3 p-3">
                  <ClinicalField label="Fasting Status" value={fastingText} />
                  <div>
                    <p className="font-bold text-gray-900 mb-1">
                      SPECIAL CONDITIONS
                    </p>
                    <div className="space-y-1">
                      <ClinicalField
                        label="Pregnancy"
                        value={isMalePatient ? "N/A" : yn(sc.pregnancy)}
                      />
                      <ClinicalField
                        label="Allergy History"
                        value={yn(sc.allergyHistory)}
                      />
                      <ClinicalField
                        label="Fainting History"
                        value={yn(sc.faintingHistory)}
                      />
                      <ClinicalField
                        label="Blood Transfusion"
                        value={yn(sc.recentBloodTransfusion)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3 p-3 border-l border-gray-200">
                  <div>
                    <p className="font-bold text-gray-900 mb-1">DRUG HISTORY</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <ClinicalField
                        label="On Medication"
                        value={yn(dh.onMedication)}
                      />
                      <ClinicalField
                        label="Antibiotics"
                        value={yn(dh.antibiotics)}
                      />
                      <ClinicalField
                        label="Blood Thinners"
                        value={yn(dh.bloodThinners)}
                      />
                      <ClinicalField label="Steroids" value={yn(dh.steroids)} />
                    </div>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 mb-1">
                      MEDICAL HISTORY
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <ClinicalField label="Diabetes" value={yn(mh.diabetes)} />
                      <ClinicalField
                        label="Hypertension"
                        value={yn(mh.hypertension)}
                      />
                      <ClinicalField
                        label="Renal Disease"
                        value={yn(mh.kidneyDisease)}
                      />
                      <ClinicalField
                        label="Liver Disease"
                        value={yn(mh.liverDisease)}
                      />
                      <ClinicalField
                        label="Thyroid"
                        value={yn(mh.thyroidDisorder)}
                      />
                      <ClinicalField
                        label="Asthma/COPD"
                        value={yn(mh.asthmaCopd)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3 p-3 border-l border-gray-200">
                  <div>
                    <p className="font-bold text-gray-900 mb-1">VIRAL MARKERS</p>
                    <div className="space-y-1">
                      <ClinicalField label="HIV Positive" value={yn(vm.hiv)} />
                      <ClinicalField label="HBsAg Positive" value={yn(vm.hbsAg)} />
                      <ClinicalField
                        label="Anti-HCV Positive"
                        value={yn(vm.hcv)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {(clinicalData.clinicalHistory || clinicalData.remarks) && (
                <div className="border-t border-gray-200 p-3 space-y-3">
                  {/* {clinicalData.clinicalHistory && (
                    <div className="flex gap-2">
                      <span className="font-bold text-gray-900 whitespace-nowrap w-32 flex-shrink-0">
                        Clinical History:
                      </span>
                      <span className="text-gray-900 font-normal">
                        {clinicalData.clinicalHistory}
                      </span>
                    </div>
                  )} */}
                  {clinicalData.remarks && (
                    <div className="flex gap-2">
                      <span className="font-bold text-gray-900 whitespace-nowrap w-32 flex-shrink-0">
                        Remarks:
                      </span>
                      <span className="text-gray-900 font-normal">
                        {clinicalData.remarks}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="mx-5 my-10 text-center">
        <button
          onClick={downloadPDF}
          className="bg-blue-600 text-white text-sm font-normal py-2 px-4 border border-gray-300 rounded cursor-pointer font-rubik hover:bg-blue-700 transition-colors"
        >
          Download PDF Report
        </button>
      </div>
      <hr className="mb-8 border-t border-gray-300" />
    </>
  );
}

TRFpopup.propTypes = {
  data: PropTypes.array,
  vialsData: PropTypes.array,
};

TRFpopup.defaultProps = {
  data: [],
  vialsData: [],
};

export default TRFpopup;